import {
  getLinkedGroupMemberListOwner,
  unlinkGroupMemberConnection,
} from '../../api/groupMemberApi';
import { useAuthStore } from '../../store/authStore';
import { useLinkedGroupMemberStore } from '../../store/linkedGroupMemberStore';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { runCurrentAccountRequest } from '../../util/currentAccountRequest';
import { canShowOwnedResource } from '../../util/scopedResource';
import { useApiWrapper } from './apiWrapper';
import { normalizeGroupMember } from './useGroupMember';

const normalizeConnection = <T extends { groupMember: Parameters<typeof normalizeGroupMember>[0] }>(
  connection: T,
) => ({
  ...connection,
  groupMember: normalizeGroupMember(connection.groupMember),
});

export const useLinkedGroupMember = () => {
  const ownerAui = useAuthStore((state) => state.user?.aui);
  const storedConnections = useLinkedGroupMemberStore((state) => state.connections);
  const listOwnerAui = useLinkedGroupMemberStore((state) => state.listOwnerAui);
  const status = useLinkedGroupMemberStore((state) => state.status);
  const setConnections = useLinkedGroupMemberStore((state) => state.setConnections);
  const setLoading = useLinkedGroupMemberStore((state) => state.setLoading);
  const setError = useLinkedGroupMemberStore((state) => state.setError);
  const removeConnection = useLinkedGroupMemberStore((state) => state.removeConnection);
  const setUpdatedTempAlert = useTempAlertStore((state) => state.setUpdatedTempAlert);
  const withApiHandler = useApiWrapper();
  const connections = canShowOwnedResource({
    pageOwnerAui: ownerAui,
    resourceOwnerAui: listOwnerAui,
    status,
  }) ? storedConnections : [];

  const getConnections = async () => {
    if (!ownerAui) return;
    const aui = ownerAui;
    setLoading(aui);
    await withApiHandler(
      () => runCurrentAccountRequest(
        aui,
        () => getLinkedGroupMemberListOwner(aui),
        (response) => setConnections(response.data.map(normalizeConnection), aui),
      ),
      [aui, 'linked-group-member-list'],
      { onError: () => setError() },
    );
  };

  const unlink = async (groupMemberId: string) => {
    if (!ownerAui) return false;
    const aui = ownerAui;
    const response = await withApiHandler(
      () => runCurrentAccountRequest(
        aui,
        () => unlinkGroupMemberConnection(aui, groupMemberId),
        (result) => result,
      ),
      [aui, 'linked-group-member-unlink', groupMemberId],
    );
    if (!response) return false;
    removeConnection(groupMemberId, aui);
    setUpdatedTempAlert();
    return true;
  };

  return {
    connections,
    status,
    getConnections,
    unlink,
  };
};
